import { cn } from '@/utils';
import { COLORS } from '@/design-tokens/colors';
import { FEATURE_FLAGS, TAG_LABELS } from '@/constants';
import { Icon } from '@/components/Icons/Icon';
import { Tag } from '@/components/Tags/Tag';
import { TIconName } from '@/design-tokens';
import { TSimulationType } from '@/types';
import React, { useMemo } from 'react';

const isV2 = FEATURE_FLAGS.ENABLE_SCORECARD_V2;

export interface PassFailTagProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * Whether to show the icon alongside the text in the tag. Defaults to true.
   */
  showIcon?: boolean;

  /**
   * The sim type determines the text of the tag.
   */
  simulationType?: TSimulationType;

  /**
   * Custom text to display for a passing score. If not provided, defaults to
   * "Pass" or "Certified" based on the simulation type.
   */
  textForPass?: string;

  /**
   * Custom text to display for a failing score. If not provided, defaults to
   * "Failed practice" or "Failed certification" based on the simulation type.
   */
  textForFail?: string;

  /**
   * Tag variant which defines the visual style and color scheme.
   * - 'pass' variant indicates a passing score and is styled with green colors.
   * - 'fail' variant indicates a failing score and is styled with red colors.
   * - 'not attempted' variant indicates that the simulation has not been attempted.
   */
  variant?: 'pass' | 'fail' | 'not attempted';
}

/**
 * PassFailTag
 *
 * A reusable component that displays a tag indicating whether a user has
 * passed or failed a simulation.
 *
 * The tag's text and color scheme are determined by the `simulationType` and `variant` props.
 * - If the simulation type is 'certification', the tag will display "Pass" or "Failed certification".
 * - If the simulation type is 'practice', it will display "Pass" or "Failed practice".
 *
 * The component does not render anything if the simulation type is
 * 'role-play' or if no variant is provided.
 */
export const PassFailTag: React.FC<PassFailTagProps> = ({
  className,
  showIcon = true,
  simulationType,
  textForPass,
  textForFail = isV2 ? TAG_LABELS.DEVELOPING : TAG_LABELS.FAIL,
  variant,
  ...props
}) => {
  /**
   * Determine the text to display for pass and fail based on the simulation type,
   * with fallbacks to custom text if provided.
   */
  const passText = useMemo(() => {
    if (textForPass !== undefined) return textForPass;
    if (simulationType === 'practice') return TAG_LABELS.READY_TO_CERTIFY;
    return TAG_LABELS.PASS;
  }, [simulationType, textForPass]);

  /**
   * Determine the text to display for fail based on the simulation type,
   * with fallbacks to custom text if provided.
   */
  const failText = useMemo(() => {
    if (textForFail) return textForFail;
    if (simulationType === 'certification') return TAG_LABELS.FAIL_CERTIFICATION;
    return TAG_LABELS.FAIL;
  }, [simulationType, textForFail]);

  /**
   * Determine the text to display in the tag based on the variant.
   * - If the variant is `'not attempted'`, it will display "No attempts".
   * - If the variant is `'pass'`, it will display the passText.
   * - If the variant is `'fail'`, it will display the failText.
   */
  const labelText = useMemo(() => {
    if (variant === 'not attempted') {
      return TAG_LABELS.NO_ATTEMPTS;
    }

    return variant === 'pass' ? passText : failText;
  }, [variant, passText, failText]);

  /**
   * Determine the icon name and color based on the variant.
   * - `'not attempted'` variant uses a warning icon with red color.
   * - `'pass'` variant uses a medal icon with green color.
   * - `'fail'` variant uses a warning icon with red color.
   */
  const iconName: TIconName = useMemo(() => {
    if (variant === 'not attempted') return 'Warning';
    return variant === 'pass' ? 'Medal' : 'Warning';
  }, [variant]);

  /**
   * Determine the icon color based on the variant. This uses the COLORS object
   * from the design tokens to ensure consistency with the design system.
   * - `'not attempted'` variant uses the red-500 color from the accent palette.
   * - `'pass'` variant uses the green-500 color from the accent palette.
   * - `'fail'` variant uses the red-500 color from the accent palette.
   */
  const iconColor = useMemo(() => {
    if (variant === 'not attempted') {
      return COLORS.neutral['gray-700'];
    }

    if (variant === 'pass') {
      return COLORS.accent['green-500'];
    }

    return isV2 ? COLORS.accent['border-yellow'] : COLORS.accent['red-500'];
  }, [variant]);

  /**
   * Determine whether to show the icon based on the simulation type and variant.
   */
  const showIconInternal = useMemo(() => {
    if (!showIcon) {
      return false;
    }

    if (simulationType === 'role-play') {
      return false;
    }

    if (simulationType === 'certification') {
      return variant === 'pass';
    }

    if (simulationType === 'practice') {
      return false;
    }

    return showIcon && variant !== 'not attempted';
  }, [showIcon, simulationType, variant]);

  // =============================================================================
  // If no variant is provided or if the simulation type is 'role-play', we don't render a tag.
  if (!variant || variant === null || !simulationType || simulationType === 'role-play') {
    return null;
  }

  /**
   * Container class names are determined by the variant,
   * with specific text colors for each state.
   */
  const containerClassNames = cn(
    {
      'text-neutral-black': variant === 'not attempted',
      'text-neutral-gray-800': variant === 'pass',
      'text-accent-border-yellow': variant === 'fail' && isV2,
      'text-accent-red-500': variant === 'fail' && !isV2,
    },
    className,
  );

  // =============================================================================
  // Render the tag with the appropriate text and styles based on the variant
  // and simulation type.
  // =============================================================================
  return (
    <div
      className={containerClassNames}
      data-testid="pass-fail-tag"
      data-type={simulationType}
      {...props}
    >
      <Tag className="gap-x-1 text-inherit" variant={variant} withBorder={true}>
        {showIconInternal && <Icon name={iconName} color={iconColor} size={12} weight="fill" />}
        {labelText}
      </Tag>
    </div>
  );
};

PassFailTag.displayName = 'PassFailTag';

export default PassFailTag;
